/* eslint-disable class-methods-use-this */
import _ from 'lodash';
import Cookies from 'js-cookie';
import { Store } from 'vuex';
import ipc from '@/renderer/scripts/utils/ipc';
import { IpcEvents } from '@/ipc-events';
import { windowEnvs } from '@/renderer/scripts/utils/ConstantConfig/PrivateConfig';

// 引入工具类
import { getChannel } from '@/renderer/scripts/composition/channel';
import ConstantConfig from '@/renderer/scripts/utils/ConstantConfig';
import smh, { SMH } from '@/renderer/scripts/smh';
import * as User from '@/renderer/scripts/utils/user/UserTyping';
import storage from '@/renderer/scripts/utils/storage/index';
import { AccessTokenManager as ATM } from '@/renderer/scripts/libs/smh-sdk-class';
import meeting from '@/renderer/scripts/utils/meeting';
import { getUrlQuery } from '@/renderer/scripts/utils/tools/index';
import { loginOauth2WithCookie } from '@/renderer/scripts/utils/user/userLogin';
import { IS_BROWSER, IS_PC, IS_PRIVATE } from '@/common/constant/platform';
import UserEventBus from './userEventBus';
import userReporter from './userReporter';
import tips from '../tips';

/**
 * 默认登录方式，web=loginWithPhone，会议里=loginWithMeetingPhone;
 * 政务云里默认=loginWithPassword
 * */
const { onlyPasswordLogin } = ConstantConfig;
let defaultLoginType: User.LoginTypeKey = onlyPasswordLogin
  ? User.LoginTypeKey.loginWithPassword
  : User.LoginTypeKey.loginWithPhone;
const defaultUserInfo = {
  baseInfo: {
    userId: -1, // 当前用户id
    isNewUser: 'unknown', // 当前用户是否新注册第一次登录的用户
    isOrgNewUser: 'unknown', // 当前用户是否当前企业里7天内接入的用户
    userToken: '', // 登录后返回的userToken 后端30天有效期
    organizations: [],
    isLogined: false,
    userRole: 'unknown',
    hasAppliedChannelOrg: true, // 是否领过渠道版，默认认为领过了，提高安全性
    needChangePassword: false, // 是否强制需要修改密码
    currentLoginedType: '', // 当前登录使用的登录方式
  },
  loginInfo: {
    loginType: defaultLoginType as User.LoginTypeKey, // 登录方式，手机号、微信... 登录完成后上报时用到
    loginFunc: '', // 登录后执行动作 '' | 'loginToJoin'(加入企业)
    loginAction: 'login', // 登录动作，分为login（直接登录）、bindPhone(绑定手机号)
    loginBindType: '', // bindPhoneInWeChat（绑定微信手机号）、bindPhoneInMeeting（绑定会议手机号）、bindPhoneInWecom（绑定企微）
    tenantKey: '', // yufu登录时用,可能是租户id或域名
    tenantType: '', // tenantName | domain
    loginFrom: '', // web pc
    authCode: '', // 回调code
    invitationCode: '', // 邀请码
    remember: false, // 登录时是否选择了记住登录
    phoneNumber: '', // 手机号登录时填入的手机号
    countryCode: '', // 手机号登录时选中的code
    wecomParams: {
      // 企微登录配置
      corpid: '',
      domain: '',
    },
    canAutoLoginOrg: false, // 是否找到可自动登入的企业
    cannotSelectOrg: false, // 不能选择企业，比如只有一个企业且被冻结或过期，需要停在登录页
    corpid: '', // 新版sso登录时需提前存一下企业id
    ssoFrom: '', // web | pc 在web还是pc打开的sso登录
    ssoAccount: {
      credential: '',
    },
    // ldap/ad登录时输入的账密 绑定手机号时用到
    ldapAccount: {
      ldapType: '',
      accountName: '',
      accountPassword: '',
    },
  },
  orgInfo: {
    organizationId: -1, // 完成登录后进入的身份id
    multiOrg: false, // 当前账号是否多身份
    libraryId: '', // 完成登录后进入的身份libraryId
    expired: false, // 当前身份是否已经过期
    editionFlag: '', // 完成登录后进入的身份editionFlag
    editionConfig: {}, // 完成登录后进入的身份editionConfig
    orgChannel: '', // 当前企业的渠道比如meeting，默认web、pc为空
  },
};

let timer: number | undefined;
const initStoreGetters = {
  userId: -1,
  organizationId: -1,
  libraryId: '',
  userToken: '',
  isNewUser: 'unknown',
  isOrgNewUser: 'unknown',
};

// userRole映射
const UserRoleMap: Record<string, string> = {
  user: 'User',
  admin: 'Admin',
  superadmin: 'SuperAdmin',
  teamadmin: 'User', // 团队管理员也是user权限
};

class UserClass extends UserEventBus {
  private inMeeting: boolean; // 是否腾讯会议环境内

  private changeRoute: (params: any, type?: string) => void;

  private store: Store<any> | { commit: Function; dispatch: Function; getters: Object };

  private spaceATM!: ATM;

  public info: User.UserInfo;

  private switchingOrg: boolean; // 是否正在切换企业流程中

  private logoutSSOUrl: string;

  constructor() {
    super();
    this.logoutSSOUrl = '';
    this.inMeeting = meeting.isTMWebview;
    this.changeRoute = _.noop;
    this.store = { commit: _.noop, dispatch: _.noop, getters: initStoreGetters };
    this.switchingOrg = false;
    if (this.inMeeting) {
      defaultLoginType = User.LoginTypeKey.loginWithMeetingPhone;
      defaultUserInfo.loginInfo.loginType = defaultLoginType;
    }
    this.info = _.cloneDeep(defaultUserInfo);
    this.initUserInfo();
    userReporter.init(this);
    this.on('after-login', this.renewToken.bind(this));
    this.on('after-logout', this.clearRenewToken.bind(this));
  }
  private checkIsOauth2() {
    const { pathname, href } = window.location;
    const { query } = getUrlQuery(href);
    const isOauth2 = (pathname === '/login' && query?.oauth2 === '1') || pathname === '/login/oauth2';
    return isOauth2;
  }

  /**
   * 所有信息加密并保存到本地
   * web端：userToken不存，怕不安全；
   * 身份列表organizations不存，怕数据太多
   */
  private saveInfoToLocal() {
    const copyInfo = _.cloneDeep(this.info);
    if (IS_BROWSER && copyInfo.baseInfo.userToken) {
      delete copyInfo.baseInfo.userToken;
    }
    if (copyInfo.baseInfo.organizations) {
      delete copyInfo.baseInfo.organizations;
    }
    storage.set(storage.StoreKey.TCD_USER_INFO, copyInfo);
  }

  // 删除本地信息
  private deleteLocalInfo() {
    storage.del(storage.StoreKey.TCD_USER_INFO);
    storage.del(storage.StoreKey.LOGIN);
    storage.del(storage.StoreKey.ORG_INFO);
  }

  // 清空用户信息,退出登录时调用
  private clearUserInfo() {
    this.info = _.cloneDeep(defaultUserInfo);
    this.store.commit('SET_INIT_USER_ACCOUNT');
    this.store.commit('SET_BASEINFO', { role: 'unknown' });
    this.deleteLocalInfo();
    if (IS_BROWSER) {
      Cookies.remove('USER_TOKEN');
    }
  }

  // 向user store同步数据
  private commitStore(info: User.FlatUserInfo) {
    const storeKeys = [
      'userId',
      'isNewUser',
      'isOrgNewUser',
      'userRole',
      'isLogined',
      'remember',
      'multiOrg',
      'hasAppliedChannelOrg',
      'organizationId',
      'expired',
      'editionFlag',
      'editionConfig',
      'loginType',
      'loginAction',
      'loginBindType',
      'orgChannel',
      'channelUpdated',
    ];
    const storeInfo: Record<string, any> = {};
    storeKeys.forEach((key: string) => {
      if (key in info) {
        storeInfo[key] = info[key];
      }
    });
    setTimeout(() => {
      this.store.commit('SET_USER_ACCOUNT', storeInfo);
    }, 0);
  }
  private initSpaceATM() {
    if ((this.info.orgInfo.organizationId as number) > 0 && this.info.baseInfo.userToken) {
      this.spaceATM.clear();
      this.spaceATM.initHttpParams({
        organizationId: this.info.orgInfo.organizationId,
        userToken: this.info.baseInfo.userToken,
        envType: IS_BROWSER ? 'web' : 'node',
      });
    }
  }

  // 找到可以登入的身份
  private getAvailableId() {
    /**
     * 展示企业列表的情况
     * 1、企业列表为[]，此时需要在企业列表里展示注册入口
     * 2、所属企业数大于1且没有找到上次登录的企业
     * 3、上次登录的企业或正在注销/冻结中/过期
     */
    const orgList = this.info.baseInfo.organizations as SMH.OrgItem[];
    let needShowOrgList = false;
    let loginOrgInfo = null;
    if (orgList.length === 0) {
      needShowOrgList = true;
    } else if (orgList.length === 1) {
      const orgInfo = orgList[0];
      const editionFlag = orgInfo?.extensionData?.editionConfig?.editionFlag || 'enterprise';

      // 渠道里只有一个企业且不可登入且没有领取过体验版时 展示企业列表
      const { channelTag } = getChannel();
      const orgDisabled = orgInfo.expired || orgInfo?.orgUser?.deregister || !orgInfo?.orgUser?.enabled;
      if (channelTag && !this.info.baseInfo.hasAppliedChannelOrg && orgDisabled) {
        return { needShowOrgList: true, loginOrgInfo };
      }

      if (orgInfo?.orgUser?.deregister) {
        // 注销中
        this.emit('org-deregistered', { orgId: orgInfo.id, editionFlag, cb: () => this.login() });
        this.setLoginInfo({ cannotSelectOrg: true });
      } else if (!orgInfo?.orgUser?.enabled) {
        // 冻结中,个人版和非个人版的文案不同
        this.emit('org-freezed', { editionFlag });
        this.setLoginInfo({ cannotSelectOrg: true });
      } else if (orgInfo?.ipLimitEnabled) {
        let isInvitation = false;
        const { invitationCode } = this.info.loginInfo;
        if (invitationCode && invitationCode.length > 0) {
          isInvitation = true;
        }
        this.emit('org-ip-restricted', { isInvitation });
        this.setLoginInfo({ cannotSelectOrg: true });
      } else {
        // 可以自动登录
        needShowOrgList = false;
        loginOrgInfo = orgInfo;
      }
    } else if (orgList.length > 1) {
      const lastLoginedOrg: SMH.OrgItem | undefined = orgList.find((item: any) => {
        const loginCountryCode = this.info.loginInfo.countryCode;
        if (loginCountryCode && loginCountryCode !== '+86') {
          // overseas phone
          if (!item.extensionData.editionConfig.enableOverseasPhoneNumber) {
            return false;
          }
        }
        return item.isLastSignedIn;
      });
      if (!lastLoginedOrg) {
        needShowOrgList = true;
      } else {
        const orgDisabled =
          lastLoginedOrg?.orgUser?.deregister ||
          !lastLoginedOrg?.orgUser?.enabled ||
          lastLoginedOrg.expired ||
          lastLoginedOrg.ipLimitEnabled;
        if (orgDisabled) {
          needShowOrgList = true;
        } else {
          // 可以自动登录
          needShowOrgList = false;
          loginOrgInfo = lastLoginedOrg;
        }
      }
    }
    return {
      needShowOrgList,
      loginOrgInfo,
    };
  }

  /**
   * 设置基本信息
   * saveLocal默认为true，代表默认存本地。特殊场景如从本地存储取出并赋值给info时saveLocal为false
   * */
  private setBaseInfo(baseInfo: User.BaseInfo, saveLocal = true) {
    this.commitStore(baseInfo);
    this.info.baseInfo = { ...this.info.baseInfo, ...baseInfo };
    saveLocal && this.saveInfoToLocal();
    // 设置cookie
    if (IS_BROWSER && baseInfo.userToken) {
      const expires = this.info.loginInfo.remember ? 10 : 1 / 12;
      Cookies.set('USER_TOKEN', baseInfo.userToken, { expires });
      // @ts-ignore TCL版本注入的Android对象，通知Android端刷新Cookies防止用户杀进程导致Cookies丢失
      if (window.NativeApp) {
        // @ts-ignore
        window.NativeApp.onAppLogin();
      }
    }
  }

  // 设置登录信息
  private setLoginInfo(loginInfo: User.LoginInfo, saveLocal = true) {
    this.commitStore(loginInfo);
    this.info.loginInfo = { ...this.info.loginInfo, ...loginInfo };
    saveLocal && this.saveInfoToLocal();
  }

  // 设置身份信息
  private setOrgInfo(orgInfo: User.OrgInfo, saveLocal = true) {
    this.commitStore(orgInfo);
    this.info.orgInfo = { ...this.info.orgInfo, ...orgInfo };
    saveLocal && this.saveInfoToLocal();
    // 特殊处理localStorage里存isLogined和orgInfo，web多窗口时监听使用, 切换企业时处理
    if ('organizationId' in orgInfo && 'libraryId' in orgInfo && this.info.baseInfo.isLogined) {
      const { organizationId, libraryId } = orgInfo;
      storage.set(storage.StoreKey.ORG_INFO, { organizationId, libraryId });
    }
  }

  // 从本地还原信息或初始化信息
  private initUserInfo(): void {
    try {
      const savedInfo = storage.get(storage.StoreKey.TCD_USER_INFO) as User.UserInfo;
      if (_.isEmpty(savedInfo)) return;
      const isOauth2 = this.checkIsOauth2();
      this.setLoginInfo({ ...savedInfo.loginInfo }, false);
      // 偶现pc端取出登录态时orgInfo为初始值，这里强行校验一下
      if (!isOauth2 && (savedInfo.baseInfo.userId === -1 || savedInfo.orgInfo.organizationId === -1)) return;
      // 会议里首次进入不需要还原本地登录态，只需要还原login信息
      if (this.inMeeting) return;
      logger.ui('[user] initUserInfo', savedInfo);
      this.setBaseInfo({ ...savedInfo.baseInfo }, false);
      this.setOrgInfo({ ...savedInfo.orgInfo }, false);
      if (IS_BROWSER) {
        const userToken = Cookies.get('USER_TOKEN');
        if (!userToken) return;
        this.info.baseInfo.userToken = userToken;
      }
    } catch (e) {
      logger.ui.error('[user] initUserInfo error', JSON.stringify(e));
      this.info = _.cloneDeep(defaultUserInfo);
    }
  }

  // 主动把本地存的userInfo恢复到this.info
  private restoreUserInfo(userData: User.UserInfo) {
    userData.baseInfo && this.setBaseInfo({ ...userData.baseInfo }, false);
    userData.loginInfo && this.setLoginInfo({ ...userData.loginInfo }, false);
    userData.orgInfo && this.setOrgInfo({ ...userData.orgInfo }, false);
    if (IS_BROWSER) {
      const userToken = Cookies.get('USER_TOKEN');
      this.info.baseInfo.userToken = userToken;
    }
    logger.ui('使用登录态登录成功...');
  }

  // web端 cookie续期
  private renewToken() {
    if (!IS_BROWSER) return;
    const userToken = Cookies.get('USER_TOKEN');
    if (!timer) {
      timer = window.setInterval(() => {
        const { remember } = this.info.loginInfo;
        const expires = remember ? 10 : 1 / 12;
        userToken && Cookies.set('USER_TOKEN', userToken, { expires });
      }, 5 * 1000);
    }
  }

  // web端 清除cookie续期定时器
  private clearRenewToken() {
    if (!IS_BROWSER) return;
    if (timer) {
      window.clearInterval(timer);
      timer = undefined;
    }
  }

  public setLogined(isLogined: boolean) {
    this.setBaseInfo({ isLogined });
    /** 保证在登录态之后再更新窗口监听 */
    // 特殊处理localStorage里存isLogined和orgInfo，web多窗口时监听使用
    const loginInfo = storage.get<{ isLogined: boolean }>(storage.StoreKey.LOGIN);
    if (loginInfo.isLogined !== isLogined) {
      storage.set(storage.StoreKey.LOGIN, { isLogined });
    }
    // 特殊处理localStorage里存isLogined和orgInfo，web多窗口时监听使用, 手动登录处理时
    const { organizationId, libraryId } = this.info.orgInfo;
    storage.set(storage.StoreKey.ORG_INFO, { organizationId, libraryId });
  }

  public setUserRole(originUserRole: string) {
    // 统一格式化userRole
    const _userRole = originUserRole || 'unknown';
    const userRole: User.UserRole = UserRoleMap[_userRole.toLowerCase()] || _userRole;
    this.setBaseInfo({ userRole });
  }

  public setIsNewUser(isNewUser: string) {
    this.setBaseInfo({ isNewUser });
  }

  public setIsOrgNewUser(isOrgNewUser: string) {
    this.setBaseInfo({ isOrgNewUser });
  }

  // 设置登录方式 只在埋点上报时用到 完成登录前设置一次即可
  public setLoginType(loginType = User.LoginTypeKey.loginWithPhone, report = false) {
    this.setLoginInfo({ loginType });
    report && this.emit('login-type-changed', loginType);
  }

  // 设置原始的登录方式
  public setCurentLoginedType(currentLoginedType = '') {
    this.setBaseInfo({ currentLoginedType });
  }

  public setDefaultLoginType() {
    const defaultType = this.inMeeting ? User.LoginTypeKey.loginWithMeetingPhone : defaultLoginType;
    this.setLoginType(defaultType);
  }

  public setLoginAction(loginAction: string) {
    this.setLoginInfo({ loginAction });
  }

  public setLoginFunc(loginFunc: string) {
    this.setLoginInfo({ loginFunc });
  }

  public setLoginBindType(loginBindType: string) {
    this.setLoginInfo({ loginBindType });
  }

  public setAuthCode(authCode: string) {
    this.setLoginInfo({ authCode });
  }

  public setInvitationCode(invitationCode: string) {
    this.setLoginInfo({ invitationCode });
  }

  public setTenantKey(tenantKey: string) {
    this.setLoginInfo({ tenantKey });
  }

  public setTenantType(tenantType: string) {
    this.setLoginInfo({ tenantType });
  }

  public setLoginFrom(loginFrom: string) {
    this.setLoginInfo({ loginFrom });
  }

  public setCountryCode(countryCode = '+86') {
    this.setLoginInfo({ countryCode });
  }

  public setPhoneNumber(phoneNumber: string) {
    this.setLoginInfo({ phoneNumber });
  }

  public setRemember(remember: boolean) {
    this.setLoginInfo({ remember });
  }

  public setWecomParams(wecomParams: User.WecomParams) {
    this.setLoginInfo({ wecomParams });
  }

  public setCorpid(corpid: string) {
    this.setLoginInfo({ corpid });
  }

  public setSSOFrom(ssoFrom: 'web' | 'pc' | '') {
    this.setLoginInfo({ ssoFrom });
  }

  public setSSOAccount(data: User.SSOAccount) {
    this.setLoginInfo({ ssoAccount: data });
  }

  public setLdapAccount(data: User.LdapAccount) {
    this.setLoginInfo({ ldapAccount: data });
  }

  public setRouteFn(routeFn: Function) {
    this.changeRoute = routeFn.bind(this);
  }
  public setStoreFn(store: Store<any>) {
    this.store = store;
  }
  public setSpaceATMFn(spaceATM: ATM) {
    this.spaceATM = spaceATM;
    return spaceATM;
  }
  public setOrgExpired(expired: boolean) {
    this.setOrgInfo({ expired });
  }

  // 设置是否领取到渠道免费版
  public setChannelApplied(hasAppliedChannelOrg: boolean) {
    this.setBaseInfo({ hasAppliedChannelOrg });
  }
  public setOrgChannel(orgChannel: string) {
    this.setOrgInfo({ orgChannel });
  }
  public setChannelUpdated(channelUpdated: boolean) {
    this.setOrgInfo({ channelUpdated });
  }
  public setNeedChangePassword(needChangePassword: boolean) {
    this.setBaseInfo({ needChangePassword });
  }
  public resetLoginInfo() {
    // 清空登录信息
    this.setLoginInfo({
      loginType: defaultLoginType,
      loginFunc: '',
      loginAction: 'login',
      loginBindType: '',
      tenantKey: '',
      tenantType: '',
      loginFrom: '',
      authCode: '',
      invitationCode: '',
      wecomParams: {
        corpid: '',
        domain: '',
      },
      canAutoLoginOrg: false,
      cannotSelectOrg: false,
    });
  }

  // 获取扁平数据结构(已知登录没有完成时，可以调用这里。比如验证成功但没有选择企业时)
  public getUserInfoSync(): User.FlatUserInfo {
    if (IS_BROWSER) {
      const userToken = Cookies.get('USER_TOKEN');
      return _.cloneDeep({
        ...this.info.baseInfo,
        ...this.info.loginInfo,
        ...this.info.orgInfo,
        userToken,
      });
    }
    return _.cloneDeep({
      ...this.info.baseInfo,
      ...this.info.loginInfo,
      ...this.info.orgInfo,
    });
  }

  // 获取扁平数据结构(已知登录已经完成时，需要用户登录态时调用)
  public getUserInfo(): User.FlatUserInfo {
    const info: User.FlatUserInfo = this.getUserInfoSync();
    const userInfoIsNotComplete =
      !info.userId ||
      info.userId < 0 ||
      !info.organizationId ||
      info.organizationId < 0 ||
      !info.libraryId ||
      !info.userToken ||
      info.isNewUser === 'unknown';
    if (userInfoIsNotComplete) {
      this.initUserInfo();
      return this.getUserInfoSync();
    }
    return info;
  }

  private async loginOut(): Promise<void> {
    const { userToken } = this.info.baseInfo;
    if (!userToken) {
      this.forceSignOut();
      return;
    }
    // 私有化sso登录退出时获取退出sso 登录 url
    try {
      if (IS_PRIVATE) {
        const { urlFrom } = windowEnvs.logoutSSOConfig;
        if (urlFrom === 'server') {
          // 接口拉取 url，比如交大
          const { data } = await smh.api.getSSOLogoutUrl({
            corpId: this.info.loginInfo.corpid,
            userToken: this.info.baseInfo.userToken,
          });
          this.logoutSSOUrl = decodeURIComponent(data.url) || '';
        } else if (urlFrom === 'local') {
          // 固定配置的 url，比如东莞理工
          this.logoutSSOUrl = windowEnvs.logoutSSOConfig.logoutSSOUrl;
        }
      }
    } catch (e) {
      logger.ui('getSSOLogoutUrl error', e);
    }
    // 退出api和退出动作一起执行
    try {
      smh.api.logout(userToken);
      this.forceSignOut();
    } catch (e) {
      logger.ui('[signOut error]', e);
    }
  }

  // 对外的退出登录方法
  public signOut(timeout?: number): void {
    // timeout默认100，需要立即退出时传0
    const t = timeout ?? 100;
    if (t === 0) {
      this.loginOut();
    } else {
      setTimeout(() => this.loginOut(), t);
    }
  }

  // 对外的强制退出方法，和signOut区别在于不需要发送请求
  public forceSignOut(): void {
    this.clearUserInfo();
    // 清除传输列表状态
    this.store.commit('SET_HAVE_UPLOAD', {
      showTrans: false,
      showList: false,
    });
    this.onLogout();
  }

  // 切换身份开始
  public switchOrgStarted(): void {
    this.switchingOrg = true;
    // 清除传输列表状态
    this.store.commit('SET_HAVE_UPLOAD', {
      showTrans: false,
      showList: false,
    });
    // 退出登录后清理本地缓存
    this.spaceATM.clear();
    this.emit('switch-org-started');
  }

  // 完成切换身份
  public switchOrgFinished(): void {
    this.switchingOrg = false;
    this.emit('switch-org-finished');
    this.emit('after-login');
  }

  // 切换身份失败
  public switchOrgFailed() {
    this.switchingOrg = false;
    this.emit('switch-org-failed');
  }

  /**
   * 账号验证完成获得userToken，开始选择企业并完成登录
   * 找到合适的企业完成登录返回true
   * 需要选择企业时返回false
   * onlyVerify 默认为true，验证并登录。只有hiflow下只需验证 不需登录
   * */
  public async verifyAndLogin(userBaseInfo: User.BaseInfo, onlyVerify?: boolean) {
    this.setBaseInfo(userBaseInfo);
    this.onVerified();
    if (onlyVerify) return;
    const logined = await this.selectOrgToLogin();
    return logined;
  }

  // 账号验证完成后 进行身份选择
  private async selectOrgToLogin() {
    const { needShowOrgList, loginOrgInfo } = this.getAvailableId();
    let logined = false;
    // 最后处理展示企业列表还是自动登录
    if (needShowOrgList) {
      this.onShowOrgList();
      this.setLoginInfo({ canAutoLoginOrg: false });
      logined = false;
    } else if (!needShowOrgList && loginOrgInfo) {
      const { needChangePassword } = loginOrgInfo.orgUser as SMH.OrgUser;
      this.setNeedChangePassword(needChangePassword);
      // 自动登录上次登录的企业
      this.setLoginInfo({ canAutoLoginOrg: true });
      const { id, libraryId, expired } = loginOrgInfo;
      const editionConfig = loginOrgInfo?.extensionData?.editionConfig;
      const editionFlag = editionConfig?.editionFlag || 'enterprise';
      const orgChannel = loginOrgInfo?.extensionData?.channelFlag;
      const orgList = this.info.baseInfo.organizations || [];
      logined = await this.setOrgAndLogin({
        organizationId: id,
        libraryId,
        editionFlag,
        editionConfig,
        expired,
        orgChannel,
        multiOrg: orgList.length > 1,
      });
    }
    return logined;
  }

  // 选择身份并登录
  public async setOrgAndLogin(orgInfo: User.OrgInfo) {
    this.setOrgInfo(orgInfo);
    this.onOrgSelected();
    const logined = await this.login();
    return logined;
  }

  // 真实的登录动作，选择企业时触发
  private async login() {
    const { organizationId = -1, editionFlag = 'enterprise' } = this.info.orgInfo;
    let logined = false;
    try {
      await smh.userAPI.loginOrg({ organizationId });
      // 这里在登录前触发一次获取基本信息 确保登录进入后已经获得
      await this.store.dispatch('getUserBaseInfo');
      if (!this.switchingOrg) {
        this.onLogined();
      }
      logined = true;
    } catch (e: MsgError) {
      // 用户账号在注销中
      if (e.code === 'OrganizationUserUnderDeregister') {
        this.emit('org-deregistered', {
          orgId: organizationId,
          editionFlag,
          cb: () => this.login(),
        });
      } else if (e.code === 'UserDisabled') {
        this.emit('org-freezed', { editionFlag });
      } else if (e.code === 'IpIsNotAllowed') {
        this.emit('org-ip-restricted', { isInvitation: false });
      }
      this.onLoginFailed(e);
    }
    return logined;
  }

  /**
   * web端退到登录页
   * web可以使用完整url进入系统，如果没有从本地取到user、login、store信息时直接退到登录页，否则进入指定路由
   * pc端打开程序默认是在登录页,如果没有从本地取到user、login、store信息时保留在登录页，否则进入指定路由
   * */
  private async backToLogin() {
    if (!IS_BROWSER) {
      this.changeRoute({ name: 'login' }, 'replace');
      return;
    }
    const { pathname, search, href } = window.location;
    const { query } = getUrlQuery(href);
    const { source, type, code, ticket } = query;
    const isOauth2 = this.checkIsOauth2();
    // 注册页直接return
    if (pathname === '/login/register') return;
    if (['/web/idassLogin', '/web/meetingLogin', '/login/sso'].includes(pathname)) return;
    if (pathname === '/' && search === '') return;
    // 第三方引流至登录页
    if (pathname === '/login' && source && type) return;
    // 微信、企微登录带code
    if (pathname === '/login' && code) return;
    // cas sso登录带ticket
    if (pathname === '/login' && ticket) return;
    // oauth2对接页面
    if (isOauth2) return;
    // 如果是meetingAuth路由，代表需要进行唤起客户端操作，故不跳转登录页
    if (pathname.includes('/web/meetingAuth')) return;
    // 如果是分享页面或者 url 参数上带有分享 id，则不跳转登录页
    if (
      pathname.includes('/web/share') ||
      pathname.includes('/web/collect') ||
      search.includes('shareToken') ||
      pathname.includes('/web/qr-connect') ||
      pathname.includes('/web/invitation') ||
      pathname.includes('/web/resetPassword')
    )
      return;
    const allowQuery: Record<string, string> = {}; // 退出登录后允许保留的路径
    ['domain_prefix', 'force_login', 'tab'].forEach(key => {
      if (query[key]) {
        allowQuery[key] = query[key];
      }
    });
    // 会议里退出登录时加标志
    const routeQuery = this.inMeeting
      ? {
          ...allowQuery,
          isLogout: '1',
        }
      : allowQuery;
    this.changeRoute({
      name: 'login',
      query: routeQuery,
    });
  }

  // 进入系统后初始化user
  public resetUserInfoFromLogin() {
    try {
      const userData = storage.get(storage.StoreKey.TCD_USER_INFO) as User.UserInfo;
      const localLogin = storage.get<{ isLogined: boolean }>(storage.StoreKey.LOGIN);
      const userDataCopy = _.cloneDeep(userData);
      // 需要修改密码 清空并退出到登录页
      if (userDataCopy?.baseInfo?.needChangePassword) {
        this.clearUserInfo();
        this.changeRoute({ name: 'login' }, 'replace');
        return;
      }
      if (userDataCopy?.baseInfo?.userToken) {
        delete userDataCopy.baseInfo.userToken;
      }
      logger.ui('[Init UserData]', userDataCopy);
      const userToken = IS_BROWSER ? Cookies.get('USER_TOKEN') : userData?.baseInfo?.userToken;
      if (
        (userData?.baseInfo?.isLogined || localLogin.isLogined) &&
        userToken &&
        (userData?.orgInfo?.organizationId as number) > 0
      ) {
        this.restoreUserInfo(userData);
        // 恢复登录态时设置登录类型为cookie登录
        this.setLoginType(User.LoginTypeKey.loginWithCookie);
        this.onLogined();
      } else {
        const isOauth2 = this.checkIsOauth2();
        // Oauth2自动登录
        if (isOauth2 && userToken && (userData?.baseInfo?.userId as number) > 0) {
          this.store.commit('setLoginingMask', true);
          loginOauth2WithCookie();
          return;
        }
        IS_BROWSER && Cookies.remove('USER_TOKEN');
        this.backToLogin();
        return;
      }
    } catch (e) {
      logger.ui('[Init UserData] error:', e);
      this.backToLogin();
      return;
    }

    if (IS_BROWSER) return;

    // pc端进入上次退出时的路由
    // 【注意】2.2.0版本开始使用TCD_ROUTE代替了ROUTE，原ROUTE会被删除。此处做兼容
    const route: CoFile.RouteData = storage.get(storage.StoreKey.ROUTE) as CoFile.RouteData;
    let newRoute: CoFile.RouteData = storage.get(storage.StoreKey.TCD_ROUTE) as CoFile.RouteData;
    logger.ui('[Init RouteData] :', route);
    logger.ui('[Init RouteData new] :', newRoute);
    if (route && !_.isEmpty(route)) {
      // 老route复制到新route并删除老的route
      newRoute = _.clone(route);
      storage.del(storage.StoreKey.ROUTE);
    }
    if (newRoute) {
      // pc端进入上次退出的路由
      if (newRoute.name === 'login') {
        // 偶现获取到了登录态，但是没获取到记录的路由，此时兜底进入工作台，免去重新登录
        this.store.commit('setRoute', { name: 'workbench' });
        this.changeRoute({ name: 'workbench' });
      } else {
        this.store.commit('setRoute', newRoute);
        this.changeRoute(newRoute);
      }
    } else {
      this.changeRoute({ name: 'workbench' });
    }
  }

  /**
   * 生命周期
   */
  onVerified() {
    // 方便排查问题，日志里打印userToken前6位
    const usertokenSuffix = (this.info.baseInfo.userToken || '').slice(0, 6);
    logger.ui(`账号验证成功，获取到usertoken：${usertokenSuffix}，userId：${this.info.baseInfo.userId}...`);
    // 取出最大的orgId，代表最新创建的企业，在统计新体验版用户时用到
    const orgIdList = (this.info.baseInfo.organizations as SMH.OrgItem[]).map(org => org.id);
    const newOrgId = orgIdList.length > 0 ? Math.max.apply(null, orgIdList) : -1;
    const newOrg = (this.info.baseInfo.organizations as SMH.OrgItem[]).find(org => org.id === newOrgId);
    const newOrgChannel = newOrg?.extensionData?.channelFlag || '';
    this.emit('verified', {
      org_id: newOrgId,
      channel_source: newOrgChannel,
    });
  }

  onShowOrgList() {
    logger.ui('没有找到合适的身份，需要手动选择...');
    this.emit('show-org-list');
  }

  onOrgSelected() {
    logger.ui(`获取到身份信息，身份id:${this.info.orgInfo.organizationId}...`);
    this.initSpaceATM();
    this.emit('org-selected');
  }

  // 必须重置密码
  onResetPassword(orgUserInfo: SMH.OrgUser) {
    this.emit('reset-password', orgUserInfo);
  }

  clearSSOCache() {
    // 私有化支持退出 sso 登录
    if (!IS_PRIVATE) return;
    if (IS_PC) {
      // pc 端清空webview的登录缓存
      ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
        event: 'clearSSOWebviewCache',
      });
    } else {
      // web 端需要调用三方平台的退出接口
      if (this.logoutSSOUrl) {
        const { target = '_self' } = windowEnvs.logoutSSOConfig;
        window.open(this.logoutSSOUrl, target);
      }
    }
  }

  onLogined() {
    // 登录成功但是需要强制修改密码
    if (this.info.baseInfo.needChangePassword) {
      const currentOrg: SMH.OrgItem = this.info.baseInfo.organizations?.find(
        org => org.id === this.info.orgInfo.organizationId,
      ) as SMH.OrgItem;
      const orgUserInfo: SMH.OrgUser = ((currentOrg || {})?.orgUser as SMH.OrgUser) || {};
      this.emit('reset-password', orgUserInfo);
      return;
    }
    const { currentLoginedType } = this.info.baseInfo;
    const { loginType } = this.info.loginInfo;
    // 第一次通过请求方式登录成功时设置一下登录方式
    if (loginType !== User.LoginTypeKey.loginWithCookie && !currentLoginedType) {
      this.setCurentLoginedType(loginType);
    }
    // 成功登录
    logger.ui('登录成功...');
    this.setLogined(true);
    this.initSpaceATM();
    this.emit('logined');
    this.emit('after-login');
  }

  onLoginFailed(e: any) {
    logger.ui('登录失败...', JSON.stringify(e));
    this.emit('login-failed', e);
    tips.error(e);
  }

  onLogout() {
    // 用户退出登录
    logger.ui('退出登录...');
    // 退出登录后清理本地缓存
    tips.closeAll();
    this.spaceATM.clear();
    this.backToLogin();
    this.emit('after-logout');
    this.clearSSOCache();
  }
}

const user = new UserClass();

export default user;

export { User, UserClass };
